import React, { useState } from "react";
import { L } from "./helpers";
import { I } from "./icons";
import { EmptyState } from "./shared/States";
import { CartCrossSell } from "./CartCrossSell";
import { useNav } from "./context/NavContext";
import { cdnImage } from "@/lib/cdnImage";
import type { CartItem, Lang, Product, Translations } from "./types";

interface CartDrawerProps {
  cartOpen: boolean;
  setCartOpen: (v: boolean) => void;
  cart: CartItem[];
  cartCnt: number;
  cartTot: number;
  updateQty: (id: CartItem["id"], qty: number, variantId?: string | null) => void;
  removeFromCart: (id: CartItem["id"], variantId?: string | null) => void;
  t: Translations;
  $: (n: number) => string;
  lang: Lang;
  /** Express 1-click: shown only when defaults are present and method is non-card. */
  expressEligible?: boolean;
  expressMethod?: "cod" | "manual_transfer" | null;
  onExpressCheckout?: () => void | Promise<void>;
  /** Full in-memory catalog for the cross-sell strip; optional so existing
   *  callers/tests that don't need recommendations keep compiling. */
  products?: Product[];
  addCart?: (p: Product, qty?: number, variantId?: string | null) => void;
}

export function CartDrawer({
  cartOpen, setCartOpen, cart, cartCnt, cartTot,
  updateQty, removeFromCart, t, $, lang,
  expressEligible = false, expressMethod = null, onExpressCheckout,
  products, addCart,
}: CartDrawerProps) {
  const { nav } = useNav();
  // Stock real: cualquier ítem con stk === 0 bloquea el checkout. `stk` puede
  // venir del catálogo local o del DB; `undefined` se trata como "sin
  // restricción" para no romper productos sin stock cargado.
  const oosItems = cart.filter((it: any) => typeof it.stk === "number" && it.stk <= 0);
  const hasOos = oosItems.length > 0;

  // Express 1-click feedback: optimistic confirmation chip + loading state on
  // the button so mobile users get instant visual feedback (the actual modal
  // takes a beat to render on slower devices).
  const [expressStatus, setExpressStatus] = useState<"idle" | "loading" | "done">("idle");
  const handleExpress = async () => {
    if (!onExpressCheckout || expressStatus !== "idle") return;
    setExpressStatus("loading");
    try {
      await onExpressCheckout();
      setExpressStatus("done");
      // Reset shortly so the chip disappears and the user can re-trigger if
      // the modal was dismissed.
      window.setTimeout(() => setExpressStatus("idle"), 1800);
    } catch (e) {
      setExpressStatus("idle");
    }
  };

  return (
    <>
      <div className={`co ${cartOpen ? "on" : ""}`} onClick={() => setCartOpen(false)} />
      <div className={`cp ${cartOpen ? "on" : ""}`}>
        <div className="chd">
          <h3>{t.yourCart} ({cartCnt})</h3>
          <button className="ccl" onClick={() => setCartOpen(false)}>{I.x}</button>
        </div>
        <div className="cbd">
          {cart.length === 0 ? (
            <EmptyState
              icon="🛒"
              title={t.cartEmpty}
              description={lang === "es" ? "Agrega productos para verlos aquí." : "Add products to see them here."}
              action={
                <button className="btn bp" type="button" onClick={() => { setCartOpen(false); nav("products"); }}>
                  {t.browseProducts || (lang === "es" ? "Explorar productos" : "Browse products")}
                </button>
              }
            />
          ) : (
            cart.map((item) => {
              const vid = (item as any).variantId ?? null;
              const rowKey = `${item.id}::${vid ?? "_"}`;
              return (
              <div key={rowKey} className="cit" data-testid={`cart-item-${item.id}`}>
                <div className="cie">
                  <img src={cdnImage(item.img, { width: 120 }) || item.img} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
                <div className="cii">
                  <b>{L(item.nm, lang)}</b>
                  {(item as any).variantName && (
                    <span style={{ fontSize: 10, color: "var(--tx3)", display: "block" }}>{(item as any).variantName}</span>
                  )}
                  {(item as any).bundleId && (item as any).bundleTitle && (
                    <span
                      data-testid={`cart-item-bundle-${item.id}`}
                      style={{
                        fontSize: 10, color: "#065F46", background: "#ECFDF5",
                        border: "1px solid #A7F3D0", padding: "2px 6px",
                        borderRadius: 4, marginTop: 4,
                        display: "inline-block", width: "fit-content",
                      }}
                    >
                      🎁 {lang === "es" ? "Paquete" : "Bundle"}: {(item as any).bundleTitle}
                    </span>
                  )}
                  <span className="cp2">
                    {$(item.pr * item.qty)}
                    {typeof (item as any).cpr === "number" && (item as any).cpr > item.pr && (
                      <span style={{ fontSize: 11, color: "var(--tx3)", textDecoration: "line-through", marginLeft: 6, fontWeight: 500 }}>
                        {$((item as any).cpr * item.qty)}
                      </span>
                    )}
                  </span>
                  {typeof (item as any).stk === "number" && (item as any).stk <= 0 && (
                    <span
                      data-testid={`cart-item-oos-${item.id}`}
                      style={{ fontSize: 10, color: "#991B1B", background: "#FEF2F2", border: "1px solid #FECACA", padding: "2px 6px", borderRadius: 4, marginTop: 4, display: "inline-block", width: "fit-content" }}
                    >
                      {lang === "es" ? "Sin stock" : "Out of stock"}
                    </span>
                  )}
                  {typeof (item as any).stk === "number" && (item as any).stk > 0 && (item as any).stk < ((item as any).low_stock_threshold ?? 10) && (
                    <span
                      data-testid={`cart-item-low-stock-${item.id}`}
                      style={{ fontSize: 10, color: "#92400E", background: "#FEF3C7", border: "1px solid #FDE68A", padding: "2px 6px", borderRadius: 4, marginTop: 4, display: "inline-block", width: "fit-content" }}
                    >
                      {lang === "es" ? "Stock bajo" : "Low stock"}
                    </span>
                  )}
                  <div className="ciq">
                    <button onClick={() => updateQty(item.id, item.qty - 1, vid)}>−</button>
                    <span>{item.qty}</span>
                    <button onClick={() => updateQty(item.id, item.qty + 1, vid)}>+</button>
                    <button className="crm" onClick={() => removeFromCart(item.id, vid)}>{t.remove}</button>
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>
        {cart.length > 0 && (
          products && addCart && (
            <CartCrossSell
              cart={cart}
              products={products}
              addCart={addCart}
              $={$}
              lang={lang}
              t={t}
            />
          )
        )}
        {cart.length > 0 && (
          <div className="cft">
            {(() => {
              const FREE_SHIP_THRESHOLD = 50; // USD
              const remaining = Math.max(0, FREE_SHIP_THRESHOLD - cartTot);
              const pct = Math.min(100, Math.round((cartTot / FREE_SHIP_THRESHOLD) * 100));
              const reached = remaining <= 0;
              return (
                <div
                  data-testid="cart-free-ship-progress"
                  style={{
                    marginBottom: 10,
                    padding: "8px 10px",
                    background: reached ? "hsl(var(--primary) / .08)" : "var(--bg3)",
                    border: `1px solid ${reached ? "hsl(var(--primary) / .35)" : "var(--bd2)"}`,
                    borderRadius: "var(--r2)",
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, color: reached ? "var(--ac3)" : "var(--tx2)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                    <span aria-hidden>🚚</span>
                    {reached
                      ? (lang === "es" ? "¡Tu pedido califica para envío GRATIS!" : "Your order qualifies for FREE shipping!")
                      : (lang === "es"
                          ? <>Te faltan <b>{$(remaining)}</b> para envío gratis</>
                          : <>You're <b>{$(remaining)}</b> away from free shipping</>)}
                  </div>
                  <div style={{ height: 6, borderRadius: 100, background: "var(--bd2)", overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary-glow, var(--primary))))",
                        transition: "width .35s cubic-bezier(.4,0,.2,1)",
                      }}
                    />
                  </div>
                </div>
              );
            })()}
            <div className="cs"><span>{t.subtotal}</span><span>{$(cartTot)}</span></div>
            <div className="cs tot"><span>{t.total}</span><span>{$(cartTot)}</span></div>
            {cart.length > 0 && (() => {
              // Deterministic mock: based on first item id so it doesn't flicker
              // on re-render. Social proof only — not a real metric.
              const seed = String(cart[0]?.id ?? "x").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
              const others = (seed % 11) + 4; // 4..14
              return (
                <div
                  data-testid="cart-social-proof"
                  style={{
                    marginTop: 6,
                    fontSize: 11,
                    color: "var(--tx3)",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span aria-hidden>🛒</span>
                  <span>
                    {lang === "es"
                      ? <>Otros <b style={{ color: "var(--tx2)" }}>{others}</b> compradores tienen este producto en su carrito</>
                      : <>Other <b style={{ color: "var(--tx2)" }}>{others}</b> shoppers have this in their cart</>}
                  </span>
                </div>
              );
            })()}
            {hasOos && (
              <div
                data-testid="cart-oos-banner"
                style={{ marginTop: 8, padding: "8px 10px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "var(--r1)", color: "#991B1B", fontSize: 11, display: "flex", alignItems: "flex-start", gap: 6 }}
              >
                <span style={{ fontSize: 14, lineHeight: 1 }}>⚠️</span>
                <span>
                  {lang === "es"
                    ? `Hay ${oosItems.length} producto(s) sin stock. Elimínalos del carrito para continuar.`
                    : `${oosItems.length} item(s) are out of stock. Remove them from the cart to continue.`}
                </span>
              </div>
            )}
            <button
              className="btn bp bf"
              data-testid="cart-checkout-btn"
              style={{ marginTop: 8 }}
              disabled={hasOos}
              title={hasOos ? (lang === "es" ? "Resuelve los productos sin stock primero" : "Resolve out-of-stock items first") : undefined}
              onClick={() => { if (hasOos) return; setCartOpen(false); nav("checkout"); }}
            >
              {t.proceedCheckout}
            </button>
            {expressEligible && expressMethod && !hasOos && onExpressCheckout && (
              <>
                <button
                  className="btn bo bf"
                  data-testid="cart-express-checkout-btn"
                  style={{
                    marginTop: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    minHeight: 40,
                    opacity: expressStatus === "loading" ? 0.85 : 1,
                    cursor: expressStatus === "loading" ? "wait" : "pointer",
                  }}
                  onClick={handleExpress}
                  disabled={expressStatus === "loading"}
                  aria-busy={expressStatus === "loading"}
                  aria-live="polite"
                  title={lang === "es"
                    ? `Pago en 1 clic con ${expressMethod === "cod" ? "contra entrega" : "transferencia"} y tu dirección guardada`
                    : `1-click checkout using ${expressMethod === "cod" ? "cash on delivery" : "bank transfer"} and your saved address`}
                >
                  {expressStatus === "loading" ? (
                    <>
                      <span
                        aria-hidden
                        style={{
                          width: 14,
                          height: 14,
                          border: "2px solid currentColor",
                          borderTopColor: "transparent",
                          borderRadius: "50%",
                          display: "inline-block",
                          animation: "nm-spin 0.7s linear infinite",
                        }}
                      />
                      <span>{lang === "es" ? "Procesando…" : "Processing…"}</span>
                    </>
                  ) : expressStatus === "done" ? (
                    <>
                      <span aria-hidden>✓</span>
                      <span>{lang === "es" ? "Confirma en la ventana" : "Confirm in the dialog"}</span>
                    </>
                  ) : (
                    <>
                      <span aria-hidden>⚡</span>
                      <span>
                        {lang === "es" ? "Comprar en 1 clic" : "Buy in 1 click"}
                        {" · "}
                        <span style={{ opacity: 0.75, fontWeight: 500 }}>
                          {expressMethod === "cod"
                            ? (lang === "es" ? "Contra entrega" : "Cash on delivery")
                            : (lang === "es" ? "Transferencia" : "Bank transfer")}
                        </span>
                      </span>
                    </>
                  )}
                </button>
                <div
                  data-testid="cart-express-hint"
                  style={{
                    marginTop: 4,
                    fontSize: 10,
                    color: "var(--tx3)",
                    textAlign: "center",
                  }}
                >
                  {lang === "es"
                    ? "Usaremos tu dirección y método guardados"
                    : "We'll use your saved address and method"}
                </div>
                <style>{`@keyframes nm-spin { to { transform: rotate(360deg); } }`}</style>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}